import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Home, Maximize2, Grid2X2, Car, Waves, Building2 } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const services = [
  {
    icon: Home,
    title: "Maison ossature bois",
    description: "Construire votre maison en ossature bois, c'est choisir une matière naturelle qui travaille avec son environnement. Légère, solide, excellente thermiquement.",
    features: ["Performance thermique élevée", "Délais de chantier réduits", "Grande liberté de conception", "Adaptabilité aux terrains"]
  },
  {
    icon: Maximize2,
    title: "Extension bois",
    description: "L'ossature bois est la solution idéale pour une extension : légère, rapide à construire et parfaitement adaptable à l'existant.",
    features: ["Intégration harmonieuse", "Rapidité d'exécution", "Préservation de l'existant", "Cohérence bioclimatique"]
  },
  {
    icon: Grid2X2,
    title: "Bardage",
    description: "Une peau protectrice qui régule les échanges thermiques, protège la structure et participe à l'identité architecturale du lieu.",
    features: ["Bois naturels", "Composites haut de gamme", "Durabilité", "Rendu authentique"]
  },
  {
    icon: Car,
    title: "Carport & Pergola",
    description: "Une transition entre l'intérieur et l'extérieur, un espace qui structure le rapport entre votre bâti et votre jardin.",
    features: ["Continuité architecturale", "Protection sans fermer", "Cohérence paysagère", "Sur mesure"]
  },
  {
    icon: Waves,
    title: "Terrasses & Plages de piscine",
    description: "Une véritable pièce de vie extérieure : bien orientée, bien dimensionnée, avec des matériaux adaptés à l'usage et au climat.",
    features: ["Bois ou composite", "Sans entretien", "Antidérapant", "Résistant aux UV"]
  },
  {
    icon: Building2,
    title: "Pool House & Tiny House",
    description: "Des projets à part entière. Petit volume, grande réflexion : implantation, orientation, rapport au paysage, performance thermique.",
    features: ["Lieux de vie complets", "Conception rigoureuse", "Cohérents et durables", "Bien implantés"]
  }
];

const avantages = [
  "Performance thermique naturellement élevée",
  "Régulation des échanges de chaleur",
  "Réduction des consommations énergétiques",
  "Délais de chantier sensiblement réduits",
  "Légèreté permettant de construire sur terrains contraignants",
  "Durée de vie estimée de 80 à 100 ans"
];

export const OssatureBois = () => {
  return (
    <div data-testid="ossature-bois-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/21378360/pexels-photo-21378360.jpeg"
            alt="Ossature bois"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/80 via-[#0a0908]/70 to-[#0a0908]"></div>
        </div>

        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Ossature Bois
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#fdfbf7] mb-8 leading-tight max-w-4xl"
          >
            Le bois, une matière naturelle et{' '}
            <span className="text-[#C5A059]">vivante</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base sm:text-lg text-[#a39c93] max-w-2xl leading-relaxed font-body"
          >
            Performant thermiquement, rapide à mettre en œuvre et adaptable à presque tous les projets : 
            le bois est un matériau technique à part entière.
          </motion.p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Notre approche</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Intégré à une stratégie{' '}
                <span className="text-[#C5A059]">bioclimatique globale</span>
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                Chez Rozier Menuiserie, nous utilisons le bois là où il a du sens, intégré à une stratégie 
                bioclimatique globale et cohérent avec l'environnement dans lequel il s'inscrit.
              </p>
              <p className="text-[#a39c93] leading-relaxed font-body">
                Associé à une isolation thermique adaptée et à des menuiseries bien positionnées, 
                il crée une enveloppe respirante, stable et durable. Un bâti qui consomme moins, 
                qui vieillit bien et qui vous ressemble.
              </p>
            </div>
            
            <div className="bg-[#1c1a17] border border-[#2a2622] p-8 sm:p-10">
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-6">
                Avantages de l'ossature bois
              </h3>
              <ul className="space-y-4">
                {avantages.map((avantage, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={18} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                    <span className="text-[#a39c93] text-sm font-body">{avantage}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="ossature-services" className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Nos réalisations</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7]">
              Des structures bois intégrées
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                data-testid={`ossature-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-[#1c1a17] border border-[#2a2622] p-8 hover:border-[#C5A059]/50 transition-colors duration-300"
              >
                <service.icon size={28} className="text-[#C5A059] mb-4" />
                <h3 className="font-heading text-xl text-[#fdfbf7] mb-3">{service.title}</h3>
                <p className="text-[#a39c93] text-sm leading-relaxed mb-6 font-body">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#a39c93] text-xs font-body">
                      <div className="w-1 h-1 bg-[#C5A059] rounded-full"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Durabilité */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="order-2 lg:order-1">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/21378360/pexels-photo-21378360.jpeg"
                  alt="Construction ossature bois"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">Durabilité</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                80 à 100 ans de{' '}
                <span className="text-[#C5A059]">durée de vie</span>
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                Bien conçue et bien protégée, la durée de vie d'une maison en ossature bois est estimée 
                à 80 à 100 ans. La clé réside dans la qualité des matériaux, la cohérence de l'enveloppe 
                et le soin apporté à la gestion de l'humidité et des jonctions.
              </p>
              <p className="text-[#a39c93] leading-relaxed font-body">
                Nous concevons des structures bois intégrées à leur implantation, à leur environnement, 
                à la stratégie bioclimatique et à la cohérence globale du projet.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#1c1a17]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7] mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-[#a39c93] max-w-2xl mx-auto mb-8 font-body">
              Votre projet ossature bois mérite d'être construit avec rigueur et cohérence. 
              Vous avez un projet de construction, d'extension ou d'aménagement en ossature bois ? 
              Parlons-en et posons ensemble les bases d'un bâti qui vous ressemble et qui dure.
            </p>
            <Link
              to="/contact"
              data-testid="ossature-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0a0908] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
